import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import GameSetup from '../components/game/GameSetup';
import ActiveGame from '../components/game/ActiveGame';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';

const GAME_STORAGE_KEY = 'nicks_name_that_tune_game_id';

const GamePage = () => {
  const [gameState, setGameState] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Try to resume existing game on mount
  useEffect(() => {
    const resumeGame = async () => {
      const savedGameId = localStorage.getItem(GAME_STORAGE_KEY);
      
      if (savedGameId) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/game/${savedGameId}`);
          
          if (response.ok) {
            const game = await response.json();
            setGameId(savedGameId);
            setGameState(game);
            toast.success('Game resumed!', {
              description: 'Welcome back! Your game is right where you left it.'
            });
          } else {
            // Game not found, clear localStorage
            localStorage.removeItem(GAME_STORAGE_KEY);
          }
        } catch (error) {
          console.error('Error resuming game:', error);
          localStorage.removeItem(GAME_STORAGE_KEY);
        }
      }
      
      setIsLoading(false);
    };

    resumeGame();
  }, []);

  const handleGameCreated = (id, game) => {
    setGameId(id);
    setGameState(game);
    // Save game ID to localStorage
    localStorage.setItem(GAME_STORAGE_KEY, id);
    toast.success('Game started! Let\'s play!');
  };

  const handleEndGame = async () => {
    if (gameId) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/game/${gameId}`, {
          method: 'DELETE',
        });
        
        if (response.ok) {
          setGameId(null);
          setGameState(null);
          // Clear localStorage
          localStorage.removeItem(GAME_STORAGE_KEY);
          toast.success('Game ended!');
        }
      } catch (error) {
        console.error('Error ending game:', error);
        toast.error('Failed to end game');
      }
    } else {
      setGameId(null);
      setGameState(null);
      localStorage.removeItem(GAME_STORAGE_KEY);
    }
  };

  const refreshGameState = async () => {
    if (gameId) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/game/${gameId}`);
        if (response.ok) {
          const data = await response.json();
          setGameState(data);
        }
      } catch (error) {
        console.error('Error refreshing game:', error);
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Game Page Header */}
      <header className="sticky top-0 z-40 border-b bg-[hsl(var(--background))]/85 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/70">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-3 brand-lockup hover:opacity-80 transition-opacity">
            <Music className="h-6 w-6 text-[hsl(var(--accent))]" />
            <h1 className="font-[var(--font-display)] text-2xl tracking-tight">
              Nick's Name That Tune
            </h1>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              Game Mode
            </span>
          </Link>

          <Link to="/">
            <Button variant="outline" className="rounded-xl" data-testid="back-to-rules-button">
              <Home className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Back to Rules</span>
              <span className="sm:hidden">Rules</span>
            </Button>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {isLoading ? (
          <div className="max-w-2xl mx-auto space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !gameState ? (
          <GameSetup onGameCreated={handleGameCreated} />
        ) : (
          <ActiveGame 
            gameId={gameId}
            gameState={gameState}
            onEndGame={handleEndGame}
            onRefresh={refreshGameState}
          />
        )}
      </div>
    </div>
  );
};

export default GamePage;
